<template>
  <section class="waitlist-section">
    <div class="container">
      <div class="waitlist-section__content">
        <div class="waitlist-section__text">
          <h2 class="waitlist-section__title">We're rolling out</h2>
          <p class="waitlist-section__description">
            Join our waitlist to be the first to know when our platform launches. 
            Start earning commissions without the paperwork hassle.
          </p>
          
          <form @submit.prevent="handleSubmit" class="waitlist-form">
            <div v-if="submitStatus === 'success'" class="form-message form-message--success">
              Thanks for your interest, we'll be in touch shortly
            </div>
            
            <div v-if="submitStatus === 'error'" class="form-message form-message--error">
              {{ errorMessage }}
            </div>
            
            <div class="form-group">
              <label for="email" class="form-label">Email address</label>
              <input
                id="email"
                v-model="formData.email"
                type="email"
                class="form-input"
                :class="{ 'form-input--error': formErrors.email }"
                placeholder="you@example.com"
                required
              />
              <div v-if="formErrors.email" class="form-error">
                {{ formErrors.email }}
              </div>
            </div>
            
            <div class="form-group">
              <label for="state" class="form-label">State</label>
              <select
                id="state"
                v-model="formData.state"
                class="form-select"
                :class="{ 'form-select--error': formErrors.state }"
                required
              >
                <option value="">Select your state</option>
                <option v-for="state in states" :key="state" :value="state">
                  {{ state }}
                </option>
              </select>
              <div v-if="formErrors.state" class="form-error">
                {{ formErrors.state }}
              </div>
            </div>
            
            <button
              type="submit"
              class="btn btn-waitlist"
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting">Joining...</span>
              <span v-else>Join the wait list</span>
            </button>
          </form>
        </div>
        
        <div class="waitlist-section__illustration">
          <img src="/assets/agent-illustration.png" alt="Agent with money and success indicators" class="agent-image" />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, reactive } from 'vue'
import { useFormValidation } from '@/composables/useFormValidation'
import { useFormSubmission } from '@/composables/useFormSubmission'

export default {
  name: 'WaitlistForm',
  setup() {
    const formData = reactive({
      email: '',
      agency_name: '',
      state: ''
    })
    
    const formErrors = reactive({})
    const isSubmitting = ref(false)
    const submitStatus = ref('idle') // 'idle', 'loading', 'success', 'error'
    const errorMessage = ref('')
    
    const states = [
      'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
      'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
      'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
      'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
      'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
      'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
      'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
      'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
      'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
      'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia'
    ]
    
    const { validateForm, clearErrors } = useFormValidation()
    const { submitForm } = useFormSubmission()
    
    const handleSubmit = async () => {
      clearErrors(formErrors)
      
      // Client-side validation
      const isValid = validateForm(formData, formErrors)
      if (!isValid) return
      
      isSubmitting.value = true
      submitStatus.value = 'loading'
      
      try {
        await submitForm(formData)
        submitStatus.value = 'success'
        // Clear form
        Object.keys(formData).forEach(key => {
          formData[key] = ''
        })
      } catch (error) {
        submitStatus.value = 'error'
        errorMessage.value = error.message || 'Something went wrong. Please try again.'
      } finally {
        isSubmitting.value = false
      }
    }
    
    return {
      formData,
      formErrors,
      isSubmitting,
      submitStatus,
      errorMessage,
      states,
      handleSubmit
    }
  }
}
</script>

<style scoped>
.waitlist-section {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  padding: 16px 0 32px 0;
}

.waitlist-section__content {
  display: flex;
  gap: 10px;
  align-items: center;
  max-width: 1200px;
  width: 100%;
}

.waitlist-section__text {
  flex: 1;
  background: white;
  padding: 64px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.waitlist-section__title {
  font-family: 'Fraunces', serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 1;
  color: #1e1e1e;
  letter-spacing: -1.2px;
  margin: 0;
  font-variation-settings: 'SOFT' 0, 'WONK' 1;
}

.waitlist-section__description {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 1.4;
  color: #757575;
  margin: 0;
  font-variation-settings: 'SOFT' 0, 'WONK' 1;
}

.waitlist-form {
  max-width: 400px;
}

.waitlist-section__illustration {
  display: flex;
  justify-content: center;
  align-items: center;
}

.waitlist-section__illustration {
  flex-shrink: 0;
}

.agent-image {
  width: 595px;
  height: 563px;
  object-fit: cover;
  object-position: 50% 50%;
}

.btn-waitlist {
  width: 100%;
  padding: var(--space-3) var(--space-6);
  background-color: var(--color-gray-700);
  color: var(--color-white);
  border: none;
  border-radius: var(--radius);
  font-weight: 500;
  font-size: var(--font-size-base);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-waitlist:hover:not(:disabled) {
  background-color: var(--color-gray-800);
}

.btn-waitlist:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 1024px) {
  .waitlist-section__content {
    grid-template-columns: 1fr;
    gap: var(--space-12);
    text-align: center;
  }
  
  .waitlist-form {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .waitlist-section {
    padding: var(--space-16) 0;
  }
  
  .waitlist-section__title {
    font-size: var(--font-size-2xl);
  }
  
  .waitlist-section__description {
    font-size: var(--font-size-base);
  }
  
  .illustration-placeholder {
    max-width: 250px;
    height: 200px;
  }
}
</style>
